import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

// ─── Utilitários ──────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  { bg: 'bg-rose-100',    text: 'text-rose-600'    },
  { bg: 'bg-amber-100',   text: 'text-amber-600'   },
  { bg: 'bg-teal-100',    text: 'text-teal-600'    },
  { bg: 'bg-cyan-100',    text: 'text-cyan-600'    },
  { bg: 'bg-orange-100',  text: 'text-orange-600'  },
  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
];

function getAvatar(id: string) {
  const n = parseInt(id.replace(/[^0-9]/g, '').slice(0, 6) || '0', 10);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

function getInitials(nome: string) {
  return nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase();
}

const AREA_COLORS: Record<string, string> = {
  'Segurança': '#10b981',
  'Administrativa': '#f59e0b',
  'Técnica': '#6366f1',
  'Tecnologia': '#0ea5e9',
  'Ambiental': '#22c55e',
  'Direito': '#f97316',
  'Saúde': '#ef4444',
  'Liderança': '#8b5cf6',
  'Comunicação': '#ec4899',
  'Urbanismo': '#84cc16',
  default: '#94a3b8',
};

function areaColor(cat: string | null): string {
  if (!cat) return AREA_COLORS.default;
  for (const key of Object.keys(AREA_COLORS)) {
    if (cat.toLowerCase().includes(key.toLowerCase())) return AREA_COLORS[key];
  }
  return AREA_COLORS.default;
}

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// ─── Componente principal ─────────────────────────────────────────────────────
export default function FuncionarioFormacaoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { participantes, formacoes, documentos, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const funcionario = funcionarios.find((f) => f.id === id);

  // Participações deste funcionário
  const minhasParticipacoes = useMemo(() =>
    participantes.filter((p) => p.funcionario_id === id),
    [participantes, id]
  );

  // Formações completas associadas
  const minhasFormacoes = useMemo(() => {
    const formacaoIds = new Set(minhasParticipacoes.map((p) => p.formacao_id));
    return formacoes
      .filter((f) => formacaoIds.has(f.id))
      .map((f) => {
        const part = minhasParticipacoes.find((p) => p.formacao_id === f.id);
        return { ...f, participacao: part };
      })
      .sort((a, b) => {
        if (!a.data_inicio) return 1;
        if (!b.data_inicio) return -1;
        return new Date(b.data_inicio).getTime() - new Date(a.data_inicio).getTime();
      });
  }, [formacoes, minhasParticipacoes]);

  // Certificados deste funcionário (por nome)
  const meusCertificados = useMemo(() => {
    if (!funcionario) return [];
    return documentos.filter(
      (d) => d.tipo === 'certificado' &&
        d.nome.toLowerCase().includes(funcionario.nome.toLowerCase())
    );
  }, [documentos, funcionario]);

  // KPIs
  const kpis = useMemo(() => {
    const totalHoras = minhasFormacoes.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
    const concluidas = minhasFormacoes.filter((f) => estadoNormalizado(f.estado) === 'concluida').length;
    const comCert = minhasParticipacoes.filter((p) => p.certificado_emitido).length;
    const taxaCert = minhasFormacoes.length > 0 ? Math.round((comCert / minhasFormacoes.length) * 100) : 0;
    const ultimaFormacao = minhasFormacoes.find((f) => estadoNormalizado(f.estado) === 'concluida');
    const diasDesdeUltima = ultimaFormacao?.data_fim
      ? Math.floor((Date.now() - new Date(ultimaFormacao.data_fim).getTime()) / (1000 * 60 * 60 * 24))
      : null;

    // Horas por área
    const horaPorArea: Record<string, number> = {};
    minhasFormacoes.forEach((f) => {
      const cat = f.categoria ?? 'Outros';
      horaPorArea[cat] = (horaPorArea[cat] ?? 0) + (f.duracao_horas ?? 0);
    });

    // Actividade por mês (2025)
    const atividadeMensal = Array(12).fill(0);
    minhasFormacoes.forEach((f) => {
      if (f.data_inicio) {
        const d = new Date(f.data_inicio);
        if (d.getFullYear() === 2025) atividadeMensal[d.getMonth()]++;
      }
    });

    return { totalHoras, concluidas, comCert, taxaCert, diasDesdeUltima, ultimaFormacao, horaPorArea, atividadeMensal };
  }, [minhasFormacoes, minhasParticipacoes]);

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          </div>
          <p className="text-[13px] text-zinc-400">A carregar...</p>
        </div>
      </main>
    );
  }

  if (!funcionario) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-user-line text-4xl text-zinc-200" />
          </div>
          <p className="text-[14px] font-semibold text-zinc-400">Funcionário não encontrado</p>
          <button
            onClick={() => navigate('/formacao/funcionarios')}
            className="whitespace-nowrap text-[13px] font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg transition-colors cursor-pointer"
          >
            ← Voltar à lista
          </button>
        </div>
      </main>
    );
  }

  const palette = getAvatar(funcionario.id);
  const maxHoraMensal = Math.max(...kpis.atividadeMensal, 1);
  const maxHoraPorArea = Math.max(...Object.values(kpis.horaPorArea), 1);

  const CARREIRA_CFG: Record<string, { bg: string; text: string }> = {
    'Técnica Superior':       { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
    'Assistente Técnico':     { bg: 'bg-amber-50',   text: 'text-amber-700'   },
    'Assistente Operacional': { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  };
  const cc = CARREIRA_CFG[funcionario.carreira] ?? CARREIRA_CFG['Técnica Superior'];

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* ── Header ── */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/formacao/funcionarios')}
              className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
              title="Voltar"
            >
              <i className="ri-arrow-left-line text-base" />
            </button>
            <div className={`w-11 h-11 flex items-center justify-center rounded-full text-[14px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
              {getInitials(funcionario.nome)}
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação · Funcionário</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">{funcionario.nome}</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">
                {funcionario.unidadeOrganica ?? '—'}
                {funcionario.subunidadeOrganica ? ` · ${funcionario.subunidadeOrganica}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${cc.bg} ${cc.text}`}>
              {funcionario.carreira}
            </span>
            {funcionario.dataAlteracaoCategoria && (
              <span className="text-[11px] text-zinc-400 flex items-center gap-1">
                <i className="ri-calendar-line text-[10px]" />
                Desde {new Date(funcionario.dataAlteracaoCategoria).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
              funcionario.status === 'ativo' ? 'bg-emerald-50 text-emerald-700' :
              funcionario.status === 'inativo' ? 'bg-rose-50 text-rose-600' :
              'bg-amber-50 text-amber-700'
            }`}>
              {funcionario.status ?? 'ativo'}
            </span>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">

        {/* ── KPIs ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-book-open-line',    label: 'Formações',          value: minhasFormacoes.length,         sub: `${kpis.concluidas} concluídas`,    accent: 'bg-zinc-100 text-zinc-600' },
            { icon: 'ri-time-line',          label: 'Total de Horas',    value: `${kpis.totalHoras}h`,           sub: `${minhasFormacoes.length > 0 ? Math.round(kpis.totalHoras / minhasFormacoes.length) : 0}h média`, accent: 'bg-zinc-100 text-zinc-600' },
            { icon: 'ri-medal-line',         label: 'Certificados',      value: kpis.comCert,                   sub: `Taxa ${kpis.taxaCert}%`,           accent: 'bg-emerald-50 text-emerald-600' },
            {
              icon: 'ri-calendar-check-line', label: 'Última Formação',
              value: kpis.diasDesdeUltima !== null ? `${kpis.diasDesdeUltima}d` : '—',
              sub: kpis.ultimaFormacao ? new Date(kpis.ultimaFormacao.data_fim ?? kpis.ultimaFormacao.data_inicio ?? '').toLocaleDateString('pt-PT') : 'Sem registos',
              accent: kpis.diasDesdeUltima !== null && kpis.diasDesdeUltima > 365 ? 'bg-amber-50 text-amber-600' : 'bg-zinc-100 text-zinc-600',
            },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-start gap-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${kpi.accent}`}>
                <i className={`${kpi.icon} text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="text-[22px] font-bold text-zinc-900 leading-tight tabular-nums">{kpi.value}</p>
                <p className="text-[12px] font-semibold text-zinc-500 mt-0.5">{kpi.label}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{kpi.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Linha 2: Actividade mensal + distribuição por área ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Actividade mensal 2025 */}
          <div className="bg-white rounded-xl border border-zinc-100 p-5">
            <h3 className="text-[13px] font-bold text-zinc-800 mb-4">Actividade por Mês (2025)</h3>
            <div className="flex items-end gap-1.5 h-[80px]">
              {kpis.atividadeMensal.map((count, i) => {
                const h = Math.round((count / maxHoraMensal) * 64);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center" style={{ height: '64px' }}>
                      <div
                        className="w-full rounded-t-sm transition-all"
                        style={{ height: count === 0 ? '3px' : `${h}px`, backgroundColor: count === 0 ? '#f4f4f5' : '#18181b' }}
                      />
                    </div>
                    <span className="text-[9px] text-zinc-400 font-medium">{MESES_ABREV[i].slice(0, 1)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Horas por área */}
          <div className="bg-white rounded-xl border border-zinc-100 p-5">
            <h3 className="text-[13px] font-bold text-zinc-800 mb-4">Horas por Área Temática</h3>
            {Object.keys(kpis.horaPorArea).length === 0 ? (
              <p className="text-[12px] text-zinc-400 text-center py-4">Sem dados</p>
            ) : (
              <div className="space-y-2.5">
                {Object.entries(kpis.horaPorArea).sort((a, b) => b[1] - a[1]).map(([cat, horas]) => (
                  <div key={cat} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: areaColor(cat) }} />
                    <p className="text-[12px] font-medium text-zinc-700 flex-1 min-w-0 truncate">{cat}</p>
                    <div className="w-24 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.round((horas / maxHoraPorArea) * 100)}%`, backgroundColor: areaColor(cat) }} />
                    </div>
                    <span className="text-[11px] font-semibold text-zinc-600 tabular-nums w-10 text-right">{horas}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Histórico de formações ── */}
        <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-50 flex items-center justify-between">
            <h3 className="text-[13px] font-bold text-zinc-800">Histórico de Formações</h3>
            <span className="text-[12px] text-zinc-400"><span className="font-semibold text-zinc-600">{minhasFormacoes.length}</span> registo(s)</span>
          </div>
          {minhasFormacoes.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-book-open-line text-3xl text-zinc-200" />
              </div>
              <p className="text-[13px] font-semibold text-zinc-400">Sem formações registadas</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-50 bg-zinc-50/50">
                  <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Formação</th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide hidden md:table-cell">Entidade</th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Data</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Horas</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Certificado</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                </tr>
              </thead>
              <tbody>
                {minhasFormacoes.map((f, i) => {
                  const isConcluida = estadoNormalizado(f.estado) === 'concluida';
                  const isPlaneada = estadoNormalizado(f.estado) === 'planeada';
                  const temCert = f.participacao?.certificado_emitido;
                  return (
                    <tr key={f.id} className={`border-b border-zinc-50 hover:bg-zinc-50/40 transition-colors ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: areaColor(f.categoria) }} />
                          <div>
                            <p className="text-[12px] font-semibold text-zinc-800 max-w-[240px] truncate">{f.nome}</p>
                            {f.categoria && <p className="text-[10px] text-zinc-400">{f.categoria}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <p className="text-[11px] text-zinc-500 max-w-[160px] truncate">{f.entidade_formadora ?? '—'}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        {f.data_inicio ? (
                          <div>
                            <p className="text-[12px] text-zinc-700">{new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                            {f.data_fim && f.data_fim !== f.data_inicio && (
                              <p className="text-[10px] text-zinc-400">→ {new Date(f.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}</p>
                            )}
                          </div>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className="text-[13px] font-bold text-zinc-700 tabular-nums">{f.duracao_horas}h</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {temCert ? (
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50">
                              <i className="ri-medal-line text-emerald-500 text-xs" />
                            </div>
                            {f.participacao?.data_certificado && (
                              <span className="text-[10px] text-zinc-400">{new Date(f.participacao.data_certificado).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          isConcluida ? 'bg-emerald-50 text-emerald-700' :
                          isPlaneada ? 'bg-zinc-100 text-zinc-500' :
                          'bg-amber-50 text-amber-700'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            isConcluida ? 'bg-emerald-500' : isPlaneada ? 'bg-zinc-400' : 'bg-amber-500'
                          }`} />
                          {isConcluida ? 'Concluída' : isPlaneada ? 'Planeada' : 'Em Curso'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Certificados no arquivo ── */}
        {meusCertificados.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-50">
              <h3 className="text-[13px] font-bold text-zinc-800">Certificados no Arquivo</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">{meusCertificados.length} certificado(s) associado(s)</p>
            </div>
            <div className="divide-y divide-zinc-50">
              {meusCertificados.map((doc) => (
                <div key={doc.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
                    <i className="ri-medal-line text-emerald-600 text-sm" />
                  </div>
                  <p className="text-[12px] font-medium text-zinc-700 flex-1 min-w-0 truncate">{doc.nome}</p>
                  {doc.created_at && (
                    <span className="text-[10px] text-zinc-400 flex-shrink-0">
                      {new Date(doc.created_at).toLocaleDateString('pt-PT')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Info pessoal ── */}
        <div className="bg-white rounded-xl border border-zinc-100 p-5">
          <h3 className="text-[13px] font-bold text-zinc-800 mb-4">Informação do Funcionário</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: 'ri-user-line',        label: 'Nome',              value: funcionario.nome },
              { icon: 'ri-briefcase-line',   label: 'Carreira',          value: funcionario.carreira },
              { icon: 'ri-calendar-line',    label: 'Carreira desde',    value: funcionario.dataAlteracaoCategoria ? new Date(funcionario.dataAlteracaoCategoria).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
              { icon: 'ri-building-2-line',  label: 'Unidade Orgânica',  value: funcionario.unidadeOrganica || '—' },
              { icon: 'ri-home-office-line', label: 'Subunidade',        value: funcionario.subunidadeOrganica || '—' },
              { icon: 'ri-mail-line',        label: 'Email',             value: funcionario.email || '—' },
              { icon: 'ri-phone-line',       label: 'Telefone',          value: funcionario.telefone || '—' },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`${item.icon} text-[11px] text-zinc-400`} />
                  </div>
                  <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">{item.label}</span>
                </div>
                <p className="text-[13px] font-medium text-zinc-700 truncate">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </main>
  );
}
